import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnInit,
  output,
  signal,
  untracked,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { RouterLink } from '@angular/router';
import { TranslocoPipe, translateSignal } from '@jsverse/transloco';
import { ButtonComponent } from '../../../../../../ui-components/lib/button/button.component';
import { SelectComponent } from '../../../../../../ui-components/lib/select/select.component';
import { ToggleComponent } from '../../../../../../ui-components/lib/toggle/toggle.component';
import { ValidationMessageComponent } from '../../../../../../ui-components/lib/validation-message/validation-message.component';
import {
  OptionItem,
  ToggleOption,
} from '../../../../../../ui-components/lib/option.interface';
import { FormService } from '../../../../../../form-builder/services/form/form.service';
import { ADMIN_ROUTES } from '../../../../../common/constants/admin.routing.constants';
import { TenantService } from '../../../../../common/services/tenant/tenant.service';
import { CustomWorkflowService } from '../custom-workflow.service';
import {
  CustomWorkflow,
  CustomWorkflowApiError,
  Localized,
  localizedGroup,
  rawValueSignal,
} from '../custom-workflow.model';
import { DualLangInputComponent } from '../../../../../../form-builder/lib/dual-lang-input/dual-lang-input.component';
import { FeatureFlagsService } from '../../../../../common/services/feature-flags/feature-flags.service';
import { FEATURE_FLAGS } from '../../../../../common/constants/feature-flags.constants';
import { ReflectionListComponent } from '../../reflections/reflection-list/reflection-list.component';
import { ReflectionScopeEnum } from '../../reflections/enums/reflection-scope.enum';

/** Backend cap on requirement value.en / value.ar. */
const REQUIREMENT_MAX_LENGTH = 500;

type RequirementRow = {
  id: number;
  form: FormGroup<{ value: ReturnType<typeof localizedGroup> }>;
};

@Component({
  selector: 'lib-custom-workflow-settings',
  imports: [
    ReactiveFormsModule,
    RouterLink,
    TranslocoPipe,
    DualLangInputComponent,
    SelectComponent,
    ToggleComponent,
    ButtonComponent,
    ValidationMessageComponent,
    ReflectionListComponent,
  ],
  templateUrl: './custom-workflow-settings.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomWorkflowSettingsComponent implements OnInit {
  readonly service = inject(CustomWorkflowService);
  /** The workflow.reflections flag: off hides everything reflection-related, on custom and built-in workflows alike. */
  protected readonly reflectionsEnabled = signal(
    inject(FeatureFlagsService).getFeatureFlagValue(
      FEATURE_FLAGS.WORKFLOW_CONFIGURATION.reflections
    )
  ).asReadonly();
  private readonly formService = inject(FormService);
  private readonly tenantService = inject(TenantService);

  protected readonly Scope = ReflectionScopeEnum;

  workflow = input.required<CustomWorkflow>();
  readonly = input<boolean>(false);
  changed = output<void>();
  apiError = output<CustomWorkflowApiError>();

  categories = signal<OptionItem[]>([]);
  isSaving = signal(false);
  editedRequirements = signal<Record<number, Localized>>({});

  detailsForm = new FormGroup({
    name: localizedGroup(),
    description: localizedGroup({}, { required: false, maxLength: 1000 }),
    category_id: new FormControl<number | null>(null, Validators.required),
    can_be_sent_back_to_first_position: new FormControl(false, {
      nonNullable: true,
    }),
  });
  newRequirement = new FormGroup({
    value: localizedGroup({}, { maxLength: REQUIREMENT_MAX_LENGTH }),
  });
  /** A form per requirement, rebuilt when the workflow reloads; rows with unsaved edits keep them. */
  requirementRows = computed<RequirementRow[]>(() => {
    const edited = untracked(this.editedRequirements);
    return this.workflow().requirements.map(r => {
      const form = new FormGroup({
        value: localizedGroup(edited[r.id] ?? r.translations.value, {
          maxLength: REQUIREMENT_MAX_LENGTH,
        }),
      });
      if (this.readonly()) form.disable();
      return { id: r.id, form };
    });
  });
  private readonly initial = signal('');
  private readonly detailsValue = rawValueSignal(this.detailsForm);
  private readonly newRequirementValue = rawValueSignal(this.newRequirement);
  dirty = computed(() => {
    const draft = this.newRequirementValue().value;
    return (
      JSON.stringify(this.detailsValue()) !== this.initial() ||
      !!(draft.en || draft.ar) ||
      Object.keys(this.editedRequirements()).length > 0
    );
  });
  permissionsPath = computed(() =>
    this.tenantService.adminPath(
      `${ADMIN_ROUTES.WORKFLOW.PERMISSIONS.DETAILS}/${this.workflow().id}/view`
    )
  );
  private readonly sendBackLabel = translateSignal(
    'custom_workflow_send_back_first_label'
  );
  sendBackToggle = computed<ToggleOption>(() => ({
    label: this.sendBackLabel() as string,
    value: true,
  }));

  ngOnInit() {
    const wf = this.workflow();
    this.detailsForm.patchValue({
      name: {
        en: wf.translations.name?.en ?? '',
        ar: wf.translations.name?.ar ?? '',
      },
      description: {
        en: wf.translations.description?.en ?? '',
        ar: wf.translations.description?.ar ?? '',
      },
      category_id: wf.category_id,
      can_be_sent_back_to_first_position: wf.can_be_sent_back_to_first_position,
    });
    if (this.readonly()) {
      // Built-in workflows only allow editing the name and description.
      this.detailsForm.controls.category_id.disable();
      this.detailsForm.controls.can_be_sent_back_to_first_position.disable();
      this.newRequirement.disable();
    }
    this.initial.set(JSON.stringify(this.detailsForm.getRawValue()));
    this.service
      .categories()
      .subscribe(res =>
        this.categories.set(
          res.data.map(c => ({ label: c.label, value: c.value }))
        )
      );
  }

  saveDetails() {
    this.formService.syncDualLangControls(this.detailsForm);
    if (this.detailsForm.invalid) {
      this.detailsForm.markAllAsTouched();
      return;
    }
    this.isSaving.set(true);
    this.service
      .update(
        this.workflow().id,
        this.detailsForm.getRawValue(),
        this.workflow().kind
      )
      .subscribe({
        next: () => {
          this.isSaving.set(false);
          this.initial.set(JSON.stringify(this.detailsForm.getRawValue()));
          this.changed.emit();
        },
        error: (error: CustomWorkflowApiError) => {
          this.isSaving.set(false);
          if (error?.errors)
            this.formService.addControlErrors(error, this.detailsForm);
          this.apiError.emit(error);
        },
      });
  }

  editRequirement(row: RequirementRow) {
    this.editedRequirements.update(map => ({
      ...map,
      [row.id]: row.form.getRawValue().value,
    }));
  }

  addRequirement() {
    this.formService.syncDualLangControls(this.newRequirement);
    if (this.newRequirement.invalid) {
      this.newRequirement.markAllAsTouched();
      return;
    }
    this.service
      .createRequirement(
        this.workflow().id,
        this.newRequirement.getRawValue().value
      )
      .subscribe({
        next: () => {
          this.newRequirement.reset();
          this.changed.emit();
        },
        error: e => this.apiError.emit(e),
      });
  }

  saveRequirement(row: RequirementRow) {
    this.formService.syncDualLangControls(row.form);
    if (row.form.invalid) {
      row.form.markAllAsTouched();
      return;
    }
    this.service
      .updateRequirement(
        this.workflow().id,
        row.id,
        row.form.getRawValue().value
      )
      .subscribe({
        next: () => {
          this.editedRequirements.update(({ [row.id]: _, ...rest }) => rest);
          this.changed.emit();
        },
        error: e => this.apiError.emit(e),
      });
  }

  deleteRequirement(id: number) {
    this.service.deleteRequirement(this.workflow().id, id).subscribe({
      next: () => this.changed.emit(),
      error: e => this.apiError.emit(e),
    });
  }
}
